import { NavLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getClients, getMe } from '../api/client';
import { useAuth } from '../auth/useAuth';
import { Brand, Logo } from './Logo';
import { Icon, IconName } from './Icon';

function navClass({ isActive }: { isActive: boolean }) {
  return [
    'flex items-center justify-center lg:justify-start gap-3 px-2.5 py-2 rounded-hs-md text-[14px] transition-colors',
    isActive
      ? 'bg-hs-surface-alt text-hs-text font-semibold'
      : 'text-hs-text-mute font-medium hover:bg-hs-surface-alt',
  ].join(' ');
}

function NavRow({ to, icon, label, count }: { to: string; icon: IconName; label: string; count?: number }) {
  return (
    <NavLink to={to} className={navClass} end>
      {({ isActive }) => (
        <>
          <Icon name={icon} size={19} strokeWidth={isActive ? 2 : 1.75} />
          <span className="sr-only lg:not-sr-only lg:inline">{label}</span>
          {typeof count === 'number' && <span className="hidden lg:inline ml-auto text-[12px] text-hs-text-mute">{count}</span>}
        </>
      )}
    </NavLink>
  );
}

export function Sidebar() {
  const { user, signOut, getIdToken } = useAuth();
  const navigate = useNavigate();

  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => getMe(getIdToken) });
  const clientsQuery = useQuery({
    queryKey: ['clients'],
    queryFn: () => getClients(getIdToken),
    enabled: meQuery.isSuccess,
  });

  const displayName = meQuery.data?.displayName ?? (user as { username?: string })?.username ?? 'You';
  const email = meQuery.data?.email ?? '';
  const initial = displayName.charAt(0).toUpperCase();
  const familyCount = clientsQuery.data?.clients.length;
  const accountType = meQuery.data?.accountType;

  async function handleSignOut() {
    await signOut();
    navigate('/login', { replace: true });
  }

  return (
    <aside className="w-[64px] lg:w-[248px] flex-shrink-0 flex flex-col bg-hs-surface border-r border-hs-border p-2 lg:p-3">
      <div className="px-1 py-3 hidden lg:block">
        <Brand />
      </div>
      <div className="flex justify-center py-3 lg:hidden">
        <Logo size={32} />
      </div>
      <div className="px-2.5 pt-2 pb-1.5 text-[10.5px] font-bold uppercase tracking-hs-overline text-hs-text-mute hidden lg:block">
        Monitoring
      </div>
      <nav className="flex flex-col gap-0.5">
        {accountType === 'community' ? (
          <NavRow to="/community" icon="family" label="Residents" count={familyCount} />
        ) : (
          <NavRow to="/clients" icon="family" label="Family" count={familyCount} />
        )}
        <NavRow to="/account" icon="account" label="Account" />
      </nav>
      <div className="mt-auto pt-3 border-t border-hs-border flex items-center justify-center lg:justify-start gap-2.5">
        <span className="w-8 h-8 rounded-full bg-hs-primary text-hs-on-primary text-[13px] font-bold flex items-center justify-center">
          {initial}
        </span>
        <span className="min-w-0 hidden lg:block">
          <span className="block text-[13px] font-semibold text-hs-text leading-tight truncate">{displayName}</span>
          {email && <span className="block text-[11.5px] text-hs-text-mute truncate">{email}</span>}
        </span>
        <button
          onClick={handleSignOut}
          aria-label="Sign out"
          className="ml-auto flex items-center justify-center w-8 h-8 rounded-hs-sm text-hs-text-mute hover:text-hs-text hover:bg-hs-surface-alt focus:outline-none focus-visible:ring-2 focus-visible:ring-hs-primary"
        >
          <Icon name="signout" size={18} />
        </button>
      </div>
    </aside>
  );
}
